import { useCallback, useEffect, useRef, useState } from 'react';
import {
  isPayabliComponentOrigin,
  loadPayabliComponent,
  type PayabliComponentConfigBase,
  type PayabliComponentInstance,
  type PayabliMode,
} from '@/lib/payments/payabli';

/**
 * How long we wait for the embedded component to finish initialization
 * before offering a retry. Long enough for a slow phone on a slow
 * network, short enough that nobody stares at a spinner wondering.
 */
export const PAYABLI_LOAD_TIMEOUT_MS = 25_000;

/**
 * Text the bundle rendered into the container where its iframe should be.
 * That is how it reports a config it refuses ("Payabli component 'card' or
 * 'ach' should be enable."): the error is caught inside the constructor and
 * written into the page, so nothing is thrown for us to catch.
 */
export function renderedComponentError(containerId: string): string | null {
  const container = document.getElementById(containerId);
  if (!container || container.querySelector('iframe')) return null;
  const message = container.textContent?.replace(/\s+/g, ' ').trim();
  return message ? message.slice(0, 300) : null;
}

const refusedMessage = (text: string) => `Payabli could not start the payment form: ${text}`;

/** One mount of the component, as the config builder and callbacks see it. */
export interface PayabliEmbedMount {
  /**
   * Identity of this mount. Callbacks compare it against the live mount before
   * acting: the bundle calls back fire-and-forget, long after a remount or a
   * Retry has thrown this instance away.
   */
  token: object;
  /** Still the mounted instance — not torn down by a remount or unmount. */
  isCurrent: () => boolean;
  /** Evidence the form is on screen (a callback fired), for bundles that stop
   *  posting mount messages. */
  markRendered: () => void;
}

/**
 * The lifecycle every Payabli embedded component on the page shares: load the
 * CDN script, construct ONE instance into a private container, learn from the
 * iframe's own messages when it rendered and when Tools/init installed its
 * payor, give up with a retryable error when it never does, and tear the
 * instance down — iframe included — on a remount.
 *
 * What the form does with the instance (tokenize a method, charge a scanned
 * check) stays in the form: it builds the config for each mount, and guards
 * its callbacks with the mount's `isCurrent`.
 *
 * `remountKey` names everything whose change must throw the instance away and
 * construct a new one. `buildConfig`, `onMount` and `onUnmount` are read from
 * the latest render, so callers may pass inline closures without remounting.
 */
export function usePayabliEmbed({
  mode,
  remountKey,
  buildConfig,
  onMount,
  onUnmount,
  onInstance,
}: {
  mode: PayabliMode;
  remountKey: string;
  buildConfig: (
    mount: PayabliEmbedMount,
  ) => Omit<PayabliComponentConfigBase, 'rootContainer'> & Record<string, unknown>;
  /** A new mount is starting: drop every piece of state about the old one. */
  onMount?: () => void;
  /** This mount is being torn down: disown whatever it still has in flight. */
  onUnmount?: (token: object) => void;
  /**
   * The component was constructed. May adjust the instance, and return what
   * undoes that; it runs when this mount is torn down. A throw here is
   * swallowed — it must never cost the payer the form.
   */
  onInstance?: (instance: PayabliComponentInstance) => (() => void) | void;
}) {
  const containerIdRef = useRef(`payabli-embed-${Math.random().toString(36).slice(2)}`);
  const instanceRef = useRef<PayabliComponentInstance | null>(null);
  /** The live mount, and the mount whose iframe acknowledged its payor. */
  const instanceTokenRef = useRef<object | null>(null);
  const initializedInstanceRef = useRef<object | null>(null);
  // Rendering and per-field validation can precede Tools/init. Only the
  // current iframe's set-token acknowledgement confirms its payor is loaded.
  const [isRendered, setIsRendered] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Bumped by Retry to re-run the mount effect from scratch.
  const [mountAttempt, setMountAttempt] = useState(0);

  // The mount effect must not depend on the callbacks' identity: callers pass
  // inline closures, so a parent re-render (e.g. typing in a sibling input)
  // would otherwise tear down and remount the iframe, wiping what the payer
  // already entered.
  const buildConfigRef = useRef(buildConfig);
  buildConfigRef.current = buildConfig;
  const onMountRef = useRef(onMount);
  onMountRef.current = onMount;
  const onUnmountRef = useRef(onUnmount);
  onUnmountRef.current = onUnmount;
  const onInstanceRef = useRef(onInstance);
  onInstanceRef.current = onInstance;

  useEffect(() => {
    // Stable for the component's lifetime, but read once here so the cleanup
    // below closes over a value rather than the ref.
    const containerId = containerIdRef.current;
    const token = {};
    instanceTokenRef.current = token;
    let cancelled = false;
    let rendered = false;
    let initialized = false;
    let undoInstance: (() => void) | null = null;
    initializedInstanceRef.current = null;
    // A remount (rail switch / retry) starts over. Every piece of state that
    // describes the OLD instance has to go with it — a stale "did not load"
    // banner over a form that is about to appear, and whatever the caller
    // tracks per instance (`onMount`).
    setIsRendered(false);
    setIsInitialized(false);
    setLoadError(null);
    onMountRef.current?.();

    const markRendered = () => {
      if (cancelled || rendered) return;
      rendered = true;
      setIsRendered(true);
    };

    const onMessage = (event: MessageEvent) => {
      if (cancelled || !isPayabliComponentOrigin(event.origin)) return;
      const frames = document.getElementById(containerId)?.querySelectorAll('iframe');
      if (!frames || !Array.from(frames).some((frame) => frame.contentWindow === event.source))
        return;
      markRendered();
      const message: unknown = event.data;
      if (
        typeof message !== 'object' ||
        message === null ||
        !('event' in message) ||
        typeof message.event !== 'string' ||
        !message.event.startsWith('callback-payabli-function-set-token') ||
        !('data' in message) ||
        typeof message.data !== 'string' ||
        !message.data.trim()
      )
        return;
      initialized = true;
      initializedInstanceRef.current = token;
      setIsInitialized(true);
      setLoadError(null);
    };
    window.addEventListener('message', onMessage);

    // A refusal the bundle writes into the container instead of throwing. Shown
    // as soon as it appears — not 25 seconds later as a connection problem —
    // and taken down if an iframe replaces it.
    let refusalShown = false;
    const checkRefusal = () => {
      if (cancelled || initialized) return;
      const refused = renderedComponentError(containerId);
      if (refused) {
        refusalShown = true;
        setLoadError(refusedMessage(refused));
      } else if (refusalShown) {
        refusalShown = false;
        setLoadError(null);
      }
    };
    const containerNode = document.getElementById(containerId);
    const observer =
      containerNode && typeof MutationObserver !== 'undefined'
        ? new MutationObserver(checkRefusal)
        : null;
    observer?.observe(containerNode!, { childList: true, subtree: true, characterData: true });

    // Nothing else watches the gap between `new PayabliComponent()` and a form
    // the payer can use: a blocked script, a wrong public token or a Payabli
    // outage used to be an infinite spinner over a dead button with no message
    // and no way out.
    const timeout = window.setTimeout(() => {
      if (cancelled || initialized) return;
      // Visible fields alone do not prove Tools/init installed the payor. Keep
      // them visible, but offer Retry rather than letting an early submit fail.
      if ((document.getElementById(containerId)?.childElementCount ?? 0) > 0) markRendered();
      const refused = renderedComponentError(containerId);
      setLoadError(
        refused
          ? refusedMessage(refused)
          : 'The secure payment form did not load completely. Check your connection (or an ad blocker) and try again.',
      );
    }, PAYABLI_LOAD_TIMEOUT_MS);

    void loadPayabliComponent(mode).then((PayabliComponent) => {
      if (cancelled) return;
      if (!PayabliComponent) {
        setLoadError('Payabli payment component failed to load.');
        return;
      }
      try {
        const instance = new PayabliComponent({
          ...buildConfigRef.current({ token, isCurrent: () => !cancelled, markRendered }),
          rootContainer: containerId,
        });
        instanceRef.current = instance;
        try {
          undoInstance = onInstanceRef.current?.(instance) ?? null;
        } catch {
          undoInstance = null;
        }
        checkRefusal();
      } catch (error) {
        setLoadError(
          error instanceof Error ? error.message : 'Failed to initialize Payabli component.',
        );
      }
    });

    return () => {
      cancelled = true;
      observer?.disconnect();
      window.clearTimeout(timeout);
      window.removeEventListener('message', onMessage);
      try {
        undoInstance?.();
      } catch {
        // Tearing down regardless.
      }
      undoInstance = null;
      instanceRef.current?.hide?.();
      instanceRef.current = null;
      // `hide()` does not reliably remove the injected iframe, and a remount
      // mounts a second one: without this the payer would end up looking at
      // two stacked forms, only one of which does anything.
      document.getElementById(containerId)?.replaceChildren();
      // Disown anything this instance still has in flight.
      if (instanceTokenRef.current === token) instanceTokenRef.current = null;
      if (initializedInstanceRef.current === token) initializedInstanceRef.current = null;
      onUnmountRef.current?.(token);
    };
  }, [mode, remountKey, mountAttempt]);

  const retry = useCallback(() => setMountAttempt((n) => n + 1), []);

  return {
    containerId: containerIdRef.current,
    instanceRef,
    instanceTokenRef,
    initializedInstanceRef,
    isRendered,
    isInitialized,
    loadError,
    retry,
  };
}
